import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useShopsFromDb } from './useShopsFromDb';
import {
  normalizeShopIds,
  buildShopAssignmentUpdates,
  persistShopAssignmentUpdates,
} from './shopAssignmentUtils';
import { isVardiyaMuduru, isMagazaMuduru, staffDisplayLabel } from './staffRoleUtils';
import './BranchAssignmentPage.css';

export default function BranchAssignmentPage({ embedded = false }) {
  const { rawRows, loading: shopsLoading, error: shopsError } = useShopsFromDb();
  const [staffRows, setStaffRows] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [savingCell, setSavingCell] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [coverageFilter, setCoverageFilter] = useState('all');
  const [pageError, setPageError] = useState('');
  const [success, setSuccess] = useState('');

  const shopsList = useMemo(
    () =>
      rawRows.map((r) => ({
        id: String(r.id),
        name: r.shop && String(r.shop).trim() ? String(r.shop).trim() : `Şube ${r.id}`,
      })),
    [rawRows],
  );

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    const { data, error } = await supabase
      .from('staff_directory')
      .select('id, full_name, staff_role, manager_kind, assigned_shop_ids')
      .order('full_name', { ascending: true });
    setStaffLoading(false);
    if (error) {
      setStaffRows([]);
      setPageError(error.message);
      return;
    }
    setStaffRows(data || []);
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const counts = useMemo(() => {
    const barista = staffRows.filter((r) => r.staff_role === 'barista').length;
    const vm = staffRows.filter((r) => isVardiyaMuduru(r)).length;
    const md = staffRows.filter((r) => isMagazaMuduru(r)).length;
    const unassigned = staffRows.filter((r) => !normalizeShopIds(r.assigned_shop_ids).length).length;
    return { total: staffRows.length, barista, vm, md, unassigned };
  }, [staffRows]);

  const shopAssignedCounts = useMemo(() => {
    const map = {};
    shopsList.forEach((s) => { map[s.id] = 0; });
    staffRows.forEach((row) => {
      normalizeShopIds(row.assigned_shop_ids).forEach((id) => {
        if (map[id] != null) map[id] += 1;
      });
    });
    return map;
  }, [staffRows, shopsList]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return staffRows.filter((row) => {
      if (roleFilter === 'barista' && row.staff_role !== 'barista') return false;
      if (roleFilter === 'vm' && !isVardiyaMuduru(row)) return false;
      if (roleFilter === 'md' && !isMagazaMuduru(row)) return false;
      if (coverageFilter === 'unassigned' && normalizeShopIds(row.assigned_shop_ids).length) return false;
      if (q && !row.full_name.toLocaleLowerCase('tr-TR').includes(q)) return false;
      return true;
    });
  }, [staffRows, search, roleFilter, coverageFilter]);

  const handleStaffSaved = (updated) => {
    setStaffRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  const toggleAssignment = async (staffId, shopId, assign) => {
    const staff = staffRows.find((r) => r.id === staffId);
    if (!staff) return;

    const cellKey = `${staffId}:${shopId}`;
    const shopLabel = shopsList.find((s) => s.id === String(shopId))?.name || 'Şube';
    const prevManager = assign && isVardiyaMuduru(staff)
      ? staffRows.find(
        (r) => isVardiyaMuduru(r)
          && r.id !== staffId
          && normalizeShopIds(r.assigned_shop_ids).includes(String(shopId)),
      )
      : null;

    setSavingCell(cellKey);
    setPageError('');
    setSuccess('');

    try {
      const updates = buildShopAssignmentUpdates(staffRows, staffId, shopId, assign);
      if (!updates.length) return;
      const saved = await persistShopAssignmentUpdates(updates);
      saved.forEach((row) => handleStaffSaved(row));

      if (assign && prevManager) {
        setSuccess(`${staff.full_name} → ${shopLabel}. ${prevManager.full_name} bu şubeden kaldırıldı.`);
      } else if (assign) {
        setSuccess(`${staff.full_name} → ${shopLabel} atandı.`);
      } else {
        setSuccess(`${staff.full_name} → ${shopLabel} kaldırıldı.`);
      }
    } catch (e) {
      setPageError(e?.message || 'Atama kaydedilemedi.');
    } finally {
      setSavingCell('');
    }
  };

  const loading = shopsLoading || staffLoading;
  const err = pageError || shopsError;
  const busy = !!savingCell;

  return (
    <div className={embedded ? 'branch-assign-page branch-assign-page--embedded' : 'admin-page-content shift-page branch-assign-page'}>
      {!embedded ? (
        <div className="admin-page-hero-actions">
          <button
            type="button"
            className="branch-assign-refresh"
            onClick={loadStaff}
            disabled={loading || busy}
          >
            {loading ? 'Yükleniyor…' : 'Yenile'}
          </button>
        </div>
      ) : null}

      {err ? <p className="branch-assign-alert branch-assign-alert--error">{err}</p> : null}
      {success ? <p className="branch-assign-alert branch-assign-alert--success">{success}</p> : null}

      {loading ? (
        <p className="branch-assign-empty">Yükleniyor…</p>
      ) : !shopsList.length ? (
        <p className="branch-assign-empty">Henüz şube yok. Mağazalar sayfasından şube ekleyin.</p>
      ) : !staffRows.length ? (
        <p className="branch-assign-empty">Önce Kişiler sekmesinden personel ekleyin.</p>
      ) : (
        <>
          <div className="branch-assign-toolbar">
            <div className="branch-assign-search-wrap">
              <svg className="branch-assign-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="search"
                className="branch-assign-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Personel ara…"
                aria-label="Personel ara"
              />
            </div>
            <div className="branch-assign-filters" role="tablist" aria-label="Rol filtresi">
              {[
                { id: 'all', label: 'Tümü', count: counts.total },
                { id: 'barista', label: 'Barista', count: counts.barista },
                { id: 'vm', label: 'VM', count: counts.vm },
                { id: 'md', label: 'MD', count: counts.md },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  role="tab"
                  aria-selected={roleFilter === opt.id}
                  className={`branch-assign-filter${roleFilter === opt.id ? ' is-active' : ''}`}
                  onClick={() => setRoleFilter(opt.id)}
                >
                  {opt.label}
                  <em>{opt.count}</em>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`branch-assign-coverage${coverageFilter === 'unassigned' ? ' is-active' : ''}`}
              onClick={() => setCoverageFilter((v) => (v === 'unassigned' ? 'all' : 'unassigned'))}
            >
              Şubesiz {counts.unassigned}
            </button>
            {embedded ? (
              <button
                type="button"
                className="branch-assign-refresh"
                onClick={loadStaff}
                disabled={loading || busy}
              >
                {loading ? 'Yükleniyor…' : 'Yenile'}
              </button>
            ) : null}
          </div>

          <div className="branch-assign-legend">
            <span className="branch-assign-legend-item">
              <i className="branch-assign-legend-dot is-vm" />
              VM · şube başına tek kişi
            </span>
            <span className="branch-assign-legend-item">
              <i className="branch-assign-legend-dot is-md" />
              MD · kısıt yok
            </span>
            <span className="branch-assign-legend-item">
              <i className="branch-assign-legend-dot is-barista" />
              Barista
            </span>
            <span className="branch-assign-meta">
              {filteredStaff.length} / {staffRows.length} kişi · {shopsList.length} şube
            </span>
          </div>

          <div className="branch-assign-table-wrap">
            <table className="branch-assign-matrix">
              <thead>
                <tr>
                  <th className="branch-assign-sticky-col">Personel</th>
                  <th className="branch-assign-role-col">Rol</th>
                  {shopsList.map((shop) => (
                    <th key={shop.id} className="branch-assign-shop-col" title={shop.name}>
                      <span>{shop.name}</span>
                      <em>{shopAssignedCounts[shop.id] || 0}</em>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredStaff.length === 0 ? (
                  <tr>
                    <td colSpan={2 + shopsList.length} className="branch-assign-empty-cell">
                      {coverageFilter === 'unassigned'
                        ? 'Şubesiz personel yok.'
                        : 'Eşleşen personel yok.'}
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map((row) => {
                    const assignedIds = normalizeShopIds(row.assigned_shop_ids);
                    const isManager = row.staff_role === 'manager';
                    const isMd = isMagazaMuduru(row);
                    return (
                      <tr
                        key={row.id}
                        className={isManager ? (isMd ? 'branch-assign-row--md' : 'branch-assign-row--manager') : ''}
                      >
                        <td className="branch-assign-sticky-col">
                          <div className="branch-assign-person">
                            <span className={`branch-assign-avatar${isManager ? (isMd ? ' is-md' : ' is-manager') : ''}`} aria-hidden>
                              {(row.full_name || '?').charAt(0).toUpperCase()}
                            </span>
                            <span className="branch-assign-person-copy">
                              <span className="branch-assign-name">{row.full_name}</span>
                              <span className="branch-assign-coverage-label">
                                {assignedIds.length ? `${assignedIds.length} şube` : 'Atanmamış'}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="branch-assign-role-col">
                          <span className={`branch-assign-role${isManager ? (isMd ? ' branch-assign-role--md' : ' branch-assign-role--manager') : ''}`}>
                            {staffDisplayLabel(row)}
                          </span>
                        </td>
                        {shopsList.map((shop) => {
                          const assigned = assignedIds.includes(shop.id);
                          const cellKey = `${row.id}:${shop.id}`;
                          const isSaving = savingCell === cellKey;
                          return (
                            <td key={shop.id} className="branch-assign-cell">
                              <label className={`branch-assign-check${assigned ? ' is-on' : ''}${isSaving ? ' is-saving' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={assigned}
                                  disabled={busy}
                                  onChange={() => toggleAssignment(row.id, shop.id, !assigned)}
                                  aria-label={`${row.full_name} — ${shop.name}`}
                                />
                                <span className="branch-assign-check-box" aria-hidden="true" />
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
